import React from 'react';
import { Icon, useToken, type IconProps as ChakraIconProps } from '@chakra-ui/react';
import { iconGradientSx, type IconGradientVariant } from '../../theme/iconGradient';

interface MaterialIconProps extends Omit<ChakraIconProps, 'children'> {
  /**
   * The name of the Material Symbol icon (2369+ available icons)
   * @example "search", "close", "add", "delete", "visibility"
   */
  name: string;
  /**
   * Whether the icon should be filled (solid) or outlined
   * @default false
   */
  filled?: boolean;
  /**
   * Weight of the icon (100-900)
   * @default 400
   */
  weight?: 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;
  /**
   * Grade adjustment for fine-tuning icon thickness (-25 to 200)
   * @default 0
   */
  grade?: number;
  /**
   * Paint the glyph in the brand ramp instead of a flat ink. Setting it also
   * suppresses this component's own `color`, because the clip needs a
   * transparent fill and the two cannot be allowed to disagree.
   *
   * Identity icons only — `theme/iconGradient.ts` carries the rule, and `cool`
   * is for magnifying-glass-shaped glyphs.
   */
  gradient?: IconGradientVariant;
}

// Renders a plain <span> with the Material Symbols font class — replaces the
// react-material-symbols React component which bundles React 18's JSX runtime.
// Forwards className/style from Chakra's Icon so color and sizing props apply.
const createMaterialSymbolComponent = (
  name: string,
  filled: boolean,
  weight: number,
  grade: number,
  fontSize: string | undefined,
) => {
  const MaterialSymbolComponent = React.forwardRef<
    HTMLSpanElement,
    React.HTMLAttributes<HTMLSpanElement>
  >((props, ref) => {
    const { className, style: externalStyle, ...rest } = props;
    const baseStyle: React.CSSProperties = {
      fontSize: fontSize || 'inherit',
      width: '1em',
      height: '1em',
      fontVariationSettings: `"FILL" ${filled ? 1 : 0}, "wght" ${weight}, "GRAD" ${grade}`,
    };

    return (
      <span
        ref={ref}
        className={className ? `material-symbols ${className}` : 'material-symbols'}
        style={{ ...baseStyle, ...externalStyle }}
        {...rest}
      >
        {name}
      </span>
    );
  });
  MaterialSymbolComponent.displayName = `MaterialSymbol(${name})`;
  return MaterialSymbolComponent;
};

type MaterialSymbolComponentType = ReturnType<typeof createMaterialSymbolComponent>;

/**
 * Component identity per (name, filled, weight, grade, fontSize).
 *
 * React reconciles by component IDENTITY, so building the `forwardRef` inline
 * on each render made every icon a DIFFERENT component type every time its
 * parent re-rendered — unmounting and remounting the `<span>` rather than
 * updating it. One filter toggle on a list of fifty rows threw away and rebuilt
 * fifty glyph nodes.
 *
 * The key space is bounded in practice by the glyphs and sizes the app ships,
 * so this is a memo rather than a cache with eviction; nothing here holds a
 * reference to anything but the five scalars it was built from.
 */
const MATERIAL_SYMBOL_COMPONENTS = new Map<string, MaterialSymbolComponentType>();

const getMaterialSymbolComponent = (
  name: string,
  filled: boolean,
  weight: number,
  grade: number,
  fontSize: string | undefined,
): MaterialSymbolComponentType => {
  const key = `${name}|${filled}|${weight}|${grade}|${fontSize ?? ''}`;
  const cached = MATERIAL_SYMBOL_COMPONENTS.get(key);
  if (cached) return cached;
  const created = createMaterialSymbolComponent(name, filled, weight, grade, fontSize);
  MATERIAL_SYMBOL_COMPONENTS.set(key, created);
  return created;
};

/**
 * MaterialIcon component that wraps Google Material Symbols with Chakra UI Icon
 *
 * Provides consistent integration with Chakra UI theming while using
 * Google's Material Symbols for visual consistency across the app.
 *
 * **Global Color Behavior (No color prop needed):**
 * - By default, all icons use `gray.600` in light mode and `gray.300` in dark mode
 * - This ensures optimal visibility in both color modes
 * - Works automatically without any additional props
 *
 * **Override when needed:**
 * - Pass `color="inherit"` to inherit from parent (useful in Tabs, Buttons, MenuItems)
 * - Pass any semantic token: `color="text.primary"`, `color="text.muted"`, etc.
 * - Pass any Chakra color: `color="info.500"`, `color="danger.400"`, etc.
 *
 * **Brand gradient:**
 * - Pass `gradient="warm"` (or `"cool"`) to paint the glyph in the brand ramp;
 *   the component then suppresses its own `color` exactly as `color="inherit"`
 *   does, because the background-clip needs a transparent fill.
 * - A caller's `sx` still wins over the gradient's own declarations, so a site
 *   can layer on top of it without losing the pairing.
 *

 * @example
 * ```tsx
 * // Default - automatically works in light and dark mode
 * <MaterialIcon name="search" />
 *
 * // Inherit from parent (for Tabs, Buttons, MenuItems, etc.)
 * <MenuItem icon={<MaterialIcon name="settings" color="inherit" />}>
 *   Settings
 * </MenuItem>
 *
 * // Explicit override
 * <MaterialIcon name="close" color="text.error" />
 * ```
 */
export const MaterialIcon: React.FC<MaterialIconProps> = ({
  name,
  filled = false,
  weight = 400,
  grade = 0,
  boxSize,
  fontSize,
  color,
  gradient,
  sx,
  ...restProps
}) => {
  // Material Symbols are text glyphs: Chakra's boxSize sets width/height, but
  // the glyph itself only changes size with fontSize. Resolve scalar size
  // tokens before applying them to the glyph; explicit fontSize still wins.
  const isScalarBoxSize = typeof boxSize === 'number' || typeof boxSize === 'string';
  const [resolvedBoxSize] = useToken('sizes', [isScalarBoxSize ? boxSize : 0]);
  const glyphSize =
    (fontSize as string | undefined) ?? (isScalarBoxSize ? String(resolvedBoxSize) : undefined);

  // Memoised so the component TYPE is stable across renders — see
  // MATERIAL_SYMBOL_COMPONENTS.
  const MaterialSymbolComponent = getMaterialSymbolComponent(
    name,
    filled,
    weight,
    grade,
    glyphSize,
  );

  // Default color: text.secondary provides gray.600 (light) / gray.400 (dark) for optimal visibility
  // Using semantic token avoids useColorModeValue which can trigger CSS generation warnings
  const resolvedColor = color ?? 'text.secondary';

  // When color is 'inherit', don't pass color prop to Icon — let parent (button) control it.
  //
  // `gradient` suppresses it too, and the honest reason is narrower than it
  // looks. MEASURED (2026-09-16, Chakra 2.10.9): Chakra serialises `sx` AFTER
  // the style props, so both land in one Emotion class and the gradient's
  // `color: transparent` overwrites a `color` prop rather than racing it — the
  // emitted CSS is byte-identical with and without this clause, same class hash.
  // So this is defensive, not load-bearing: it keeps the component from emitting
  // a declaration it intends to lose, and it holds the invariant if that
  // ordering ever changes. Do NOT describe it as the thing that makes the pair
  // inseparable — what does that is the PROP, which is impossible to half-apply
  // because a caller passes one word instead of two.
  const iconColor = gradient || resolvedColor === 'inherit' ? undefined : resolvedColor;

  // Gradient declarations go UNDER the caller's `sx` so a call site can still
  // layer on top of them.
  const resolvedSx = gradient ? { ...iconGradientSx(gradient), ...sx } : sx;

  // Material Symbols renders the icon NAME as ligature text, so without this
  // every ancestor that derives its accessible name from contents inherits the
  // raw glyph name — an icon+label button announces "homeHome", and a bare
  // icon control announces "star" instead of what it does.
  //
  // Decorative by default — the neighbouring visible label names the control,
  // and the glyph name must not join it.
  //
  // When a caller passes aria-label the icon IS the meaning, so it gets
  // role="img" to be exposed as an image rather than an anonymous generic. Note
  // what this does NOT fix: a labelled descendant already contributes its label
  // to an ancestor naming from contents (accname §2F), so parent naming worked
  // before too. The value here is the icon's own exposure, plus the default
  // branch above.
  const hasAriaLabel = restProps['aria-label'] != null || restProps['aria-labelledby'] != null;
  const a11yProps = hasAriaLabel ? { role: 'img' } : { 'aria-hidden': true };

  return (
    <Icon
      as={MaterialSymbolComponent}
      boxSize={boxSize}
      fontSize={fontSize}
      color={iconColor}
      sx={resolvedSx}
      {...a11yProps}
      {...restProps}
    />
  );
};
